
import Vue from 'vue';
import BaseIcon from '@core/components/UI/BaseIcon.vue';
import { HeadlineWithRelatedArticles } from '@headlines/types/headline';
import { gridConfig } from '@core/config/grid.config';
import { iconsConfig, iconList, careerIcons, careerTags, detectorCategories, politicalAdCategories } from '@headlines/config/icons.config';

import dayjs from 'dayjs';
import 'dayjs/locale/lt';
import isBetween from 'dayjs/plugin/isBetween';

dayjs.locale('lt');
dayjs.extend(isBetween);

interface Props {
  headline: HeadlineWithRelatedArticles;
  size: string;
}

interface Data {
  iconList: Record<string, string>;
  contentType: {
    audio: boolean;
    awkwardQuestions: boolean;
    bloomberg: boolean;
    breakingNews: boolean;
    career: boolean;
    detector: boolean;
    detectorCategory: boolean;
    economist: boolean;
    everydayHeroes: boolean;
    exclusive: boolean;
    gallery: boolean;
    graph: boolean;
    important: boolean;
    interview: boolean;
    invest: boolean;
    liveBlog: boolean;
    politicalAd: boolean;
    premiere: boolean;
    projectSyndicate: boolean;
    renew: boolean;
    research: boolean;
    specialProject: boolean;
    sponsoredContent: boolean;
    stream: boolean;
    streamSoon: boolean;
    sustainableCourse: boolean;
    test: boolean;
    video: boolean;
  };
}

interface Computed {
  isMobileView: boolean;
  absoluteIcons: any;
  flexIcons: any;
  isLive: boolean;
  isLiveSoon: boolean;
}

interface Methods {
  hasIcon: (icon: string) => boolean;
  hasTagId: (tagId: number) => boolean;
  isPrimaryCategory: (categoryId: number) => boolean;
  isNoPadding: (name: string) => boolean;
  getClassModifier: (name: string) => string[];
}

interface Process {
  client: boolean;
}

declare let process: Process;

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    BaseIcon,
  },
  props: {
    headline: {
      type: Object,
      required: true,
    },
    size: {
      type: String,
      required: false,
      default: 'default',
    },
  },
  data() {
    return {
      iconList,
      contentType: {
        audio:
          this.headline.relatedEntities?.hasAudio || !!this.headline.settings?.find((setting) => setting.key === 'frontPageAudio' && setting.value === true),
        awkwardQuestions:
          this.headline.settings?.find((setting) => setting.key === 'sources')?.value.length &&
          this.headline.settings?.find((setting) => setting.key === 'sources')?.value[0]['name'] === 'Nepatogūs klausimai',
        bloomberg:
          this.headline.primaryCategory.id === 79298779 ||
          !!this.headline.tags?.items?.find((tag) => tag.id === 82146369) ||
          (this.headline.settings?.find((setting) => setting.key === 'sources')?.value.length &&
            this.headline.settings?.find((setting) => setting.key === 'sources')?.value[0]['name'] === 'Bloomberg'),
        breakingNews: !!this.headline.settings?.find((setting) => setting.key === 'frontPageBreakingNews' && setting.value === true),
        career:
          !!this.headline.tags?.items?.find((tag) => careerTags.includes(tag.id)) ||
          !!this.headline.settings?.find((setting) => careerIcons.includes(setting.key) && setting.value === true),
        detector: this.headline.primaryCategory.parentCategory?.id === 85827763 || this.headline.primaryCategory.parentCategory?.id === 79440681,
        detectorCategory: detectorCategories.includes(this.headline.primaryCategory?.id),
        economist:
          !!this.headline.tags?.items?.find((tag) => tag.id === 49711978) ||
          (this.headline.settings?.find((setting) => setting.key === 'sources')?.value.length &&
            this.headline.settings?.find((setting) => setting.key === 'sources')?.value[0]['name'] === 'The Economist'),
        everydayHeroes:
          this.headline.settings?.find((setting) => setting.key === 'sources')?.value.length &&
          this.headline.settings?.find((setting) => setting.key === 'sources')?.value[0]['name'] === 'Kasdienybės herojai',
        exclusive: this.headline.settings?.find((setting) => setting.key === 'icon.exclusive')?.value ?? false,
        gallery:
          this.headline.relatedEntities?.hasGallery ||
          (this.headline.settings?.find((setting) => setting.key === 'icon.image')?.value ?? false) ||
          !!this.headline.settings?.find((setting) => setting.key === 'frontPageImage' && setting.value === true),
        graph:
          (this.headline.settings?.find((setting) => setting.key === 'icon.graph')?.value ?? false) ||
          !!this.headline.settings?.find((setting) => setting.key === 'frontPageGraph' && setting.value === true),
        important:
          (this.headline.settings?.find((setting) => setting.key === 'icon.important')?.value ?? false) ||
          !!this.headline.settings?.find((setting) => setting.key === 'frontPageImportant' && setting.value === true),
        interview: !!this.headline.settings?.find((setting) => setting.key === 'frontPageInterview' && setting.value === true),
        invest:
          !!this.headline.settings?.find((setting) => setting.key === 'frontPageInvest' && setting.value === true) ||
          (this.headline.settings?.find((setting) => setting.key === 'sources')?.value.length &&
            this.headline.settings?.find((setting) => setting.key === 'sources')?.value[0]['name'] === 'Žurnalas „Investuok"'),
        liveBlog: !!this.headline.settings?.find((setting) => setting.key === 'articleLiveBlog' && setting.value === true),
        politicalAd:
          politicalAdCategories.includes(this.headline.primaryCategory?.id) ||
          !!this.headline.tags?.items?.find((tag) => tag.id === 71865184) ||
          !!this.headline.settings?.find((setting) => setting.key === 'frontPagePoliticalAd' && setting.value === true),
        projectSyndicate:
          this.headline.settings?.find((setting) => setting.key === 'sources')?.value.length &&
          this.headline.settings?.find((setting) => setting.key === 'sources')?.value[0]['name'] === 'Project Syndicate',
        premiere:
          !!this.headline.settings?.find((setting) => setting.key === 'frontPagePremiere' && setting.value === true) ||
          !!(
            this.headline.content.paywall?.enabled &&
            this.headline.content.paywall.disableAt &&
            new Date() < new Date(this.headline.content.paywall.disableAt)
          ),
        renew:
          (this.headline.settings?.find((setting) => setting.key === 'icon.renewed')?.value ?? false) ||
          !!this.headline.settings?.find((setting) => setting.key === 'frontPageRenewed' && setting.value === true),
        research:
          (this.headline.settings?.find((setting) => setting.key === 'icon.research')?.value ?? false) ||
          !!this.headline.settings?.find((setting) => setting.key === 'frontPageResearch' && setting.value === true) ||
          !!this.headline.settings?.find((setting) => setting.key === 'frontPageInvestigativeJournalism' && setting.value === true),
        specialProject:
          (this.headline.settings?.find((setting) => setting.key === 'icon.spec_project')?.value ?? false) ||
          !!this.headline.settings?.find((setting) => setting.key === 'specialProject' && setting.value === true),
        sponsoredContent:
          (this.headline.settings?.find((setting) => setting.key === 'icon.sponsored_content')?.value ?? false) ||
          !!this.headline.settings?.find((setting) => setting.key === 'frontPageSponsoredContent' && setting.value === true),
        stream:
          this.headline.relatedEntities?.hasStream ||
          !!this.headline.settings?.find((setting) => setting.key === 'frontPageLiveVideo' && setting.value === true),
        streamSoon: this.headline.settings?.find((setting) => setting.key === 'streamSoon')?.value ?? false,
        sustainableCourse:
          !!this.headline.tags?.items?.find((tag) => [91160101].includes(tag.id)) ||
          !!this.headline.settings?.find((setting) => setting.key === 'frontPageSustainabilityCourse' && setting.value === true),
        test: !!this.headline.settings?.find((setting) => setting.key === 'frontPageTest' && setting.value === true),
        video:
          ((this.headline.settings?.find((setting) => setting.key === 'icon.video')?.value ?? false) &&
            this.headline.primaryCategory?.channel.url !== 'www.delfi.lt/video') ||
          (this.headline.relatedEntities?.hasVideo && this.headline.primaryCategory?.channel.url !== 'www.delfi.lt/video') ||
          !!this.headline.settings?.find((setting) => setting.key === 'frontPageVideo' && setting.value === true),
      },
    };
  },
  computed: {
    isMobileView() {
      if (process.client) {
        const isIpad = (/Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints && navigator.maxTouchPoints > 1) as boolean;

        return window.screen.width <= gridConfig.breakpoints.lg || isIpad;
      } else {
        return this.$ua && (this.$ua.isFromSmartphone() || this.$ua.isFromMobilephone() || this.$ua.isFromTablet());
      }
    },
    isLive() {
      const streamEventStart = this.headline.settings.find((setting: { key: string }) => setting.key === 'streamEventStart')?.value ?? false;
      const streamEventEnd = this.headline.settings.find((setting: { key: string }) => setting.key === 'streamEventEnd')?.value ?? false;

      if (!streamEventStart || !streamEventEnd) {
        return false;
      }

      return dayjs().isBetween(dayjs(streamEventStart), dayjs(streamEventEnd), 's');
    },
    isLiveSoon() {
      const streamEventStart = this.headline.settings.find((setting: { key: string }) => setting.key === 'streamEventStart')?.value ?? false;
      const streamEventEnd = this.headline.settings.find((setting: { key: string }) => setting.key === 'streamEventEnd')?.value ?? false;

      if (!streamEventStart || !streamEventEnd) {
        return false;
      }

      return dayjs().isBetween(dayjs(streamEventStart).subtract(30, 'minutes'), dayjs(streamEventStart), 's');
    },
    absoluteIcons() {
      const icons: string[] = [];

      Object.entries(this.contentType).forEach(([name, isEnabled]) => {
        const icon = iconsConfig[name];

        if (icon && isEnabled && icon.position && (icon.position === 'left' || icon.position === 'bottom')) {
          icons.push(name);
        }
      });

      // Detect legacy streams
      if (this.isLive) {
        icons.push('stream');
      }

      if (this.isLiveSoon) {
        icons.push('streamSoon');
      }

      return icons;
    },
    flexIcons() {
      const icons: string[] = [];

      Object.entries(this.contentType).forEach(([name, isEnabled]) => {
        const icon = iconsConfig[name];

        if (icon && isEnabled && icon.position && icon.position === 'right') {
          icons.push(name);
        }
      });

      return icons;
    },
  },
  methods: {
    hasIcon(icon) {
      return !!this.headline.settings?.find((setting) => setting.key === icon && setting.value === true);
    },
    hasTagId(tagId) {
      return !!this.headline.tags?.items?.find((tag) => tag.id === tagId);
    },
    isPrimaryCategory(categoryId) {
      return this.headline.primaryCategory.id === categoryId;
    },
    isNoPadding(name) {
      const noPaddingIcons: string[] = ['career', 'exclusive', 'political', 'sustainableCourse', 'research', 'invest'];

      return this.isMobileView && noPaddingIcons.includes(name);
    },
    getClassModifier(name) {
      const modifiers: string[] = [];

      const icon = iconsConfig[name];
      if (icon) {
        if (icon.type && icon.type !== 'default') {
          modifiers.push(`C-headline-icons__icon--${icon.type}`);
        }

        if (icon.position && icon.position !== 'right') {
          modifiers.push(`C-headline-icons__icon--${icon.position}`);
        }

        if (icon.color) {
          modifiers.push(`C-headline-icons__icon--${icon.color}`);
        }
      }

      modifiers.push(`C-headline-icons__icon--${name}`);

      return modifiers;
    },
  },
});
